import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { BehaviorSubject } from "rxjs";

export type Theme = "light" | "dark";

@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private renderer: Renderer2;
  private currentThemeSubject = new BehaviorSubject<Theme>(
    this.getInitialTheme()
  );
  public currentTheme$ = this.currentThemeSubject.asObservable();

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Resuelve el tema inicial. El criterio DEBE coincidir con `src/assets/theme-boot.js`,
   * que aplica la clase al <body> antes del primer pintado para evitar el flash.
   */
  private getInitialTheme(): Theme {
    // 1) Preferencia explicita del usuario.
    const savedTheme = this.readStoredTheme();
    if (savedTheme) {
      return savedTheme;
    }

    // 2) Preferencia del sistema operativo.
    if (this.prefersDarkScheme()) {
      return "dark";
    }

    // 3) Por defecto, tema claro.
    return "light";
  }

  private readStoredTheme(): Theme | null {
    try {
      const saved = localStorage.getItem("theme");
      return saved === "light" || saved === "dark" ? saved : null;
    } catch {
      // localStorage puede lanzar en modo privado o con cookies bloqueadas.
      return null;
    }
  }

  private prefersDarkScheme(): boolean {
    try {
      return Boolean(
        window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
      );
    } catch {
      return false;
    }
  }

  public setTheme(theme: Theme): void {
    this.currentThemeSubject.next(theme);
    localStorage.setItem("theme", theme);

    // Aplicar tema al documento
    if (theme === "dark") {
      this.renderer.addClass(document.body, "dark-theme");
      this.renderer.removeClass(document.body, "light-theme");
    } else {
      this.renderer.addClass(document.body, "light-theme");
      this.renderer.removeClass(document.body, "dark-theme");
    }
  }

  public toggleTheme(): void {
    const currentTheme = this.currentThemeSubject.value;
    const newTheme: Theme = currentTheme === "light" ? "dark" : "light";
    this.setTheme(newTheme);
  }

  public initializeTheme(): void {
    this.setTheme(this.currentThemeSubject.value);
  }

  // ✅ Nuevo método para acceder al valor actual del tema
  public getCurrentTheme(): Theme {
    return this.currentThemeSubject.value;
  }
}
